import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AutocompleteCombobox } from "@/components/ui/autocomplete-combobox";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  StandardDialog,
  StandardDialogBody,
  StandardDialogContent,
  StandardDialogDescription,
  StandardDialogFooter,
  StandardDialogHeader,
  StandardDialogTitle,
} from "@/components/ui/standard-dialog";
import { useCreateDiscipline } from "@/hooks/useDisciplines";
import { useTranslation } from "@/hooks/useTranslation";
import { DISCIPLINE_CATEGORIES, type Discipline } from "@/types/people";
import { LabelWithError } from "./person-form/fields";

const createSchema = (t: (key: string) => string) =>
  z.object({
    name: z.string().trim().min(1, t("form.discipline.nameRequired")),
    description: z.string(),
    category: z.string(),
    color: z.string(),
  });

type CreateDisciplineValues = z.infer<ReturnType<typeof createSchema>>;

interface CreateDisciplineDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  /** The org's disciplines — a typed name that matches one is reused, not duplicated. */
  disciplines: Discipline[];
  /** The discipline to add to the person. */
  onCreated: (discipline: Pick<Discipline, "id" | "name">) => void;
}

/**
 * Create a discipline in the org vocabulary (or reuse one of the same name)
 * and add it to the person being edited. Mount it only while open. The
 * submit handler stops propagation — React submit events bubble through the
 * portal to the person form around it.
 */
export function CreateDisciplineDialog({
  open,
  onOpenChange,
  organizationId,
  disciplines,
  onCreated,
}: CreateDisciplineDialogProps) {
  const { t } = useTranslation("people");
  const createDiscipline = useCreateDiscipline();
  const schema = useMemo(() => createSchema(t), [t]);

  const form = useForm<CreateDisciplineValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", description: "", category: "", color: "#6B7280" },
  });

  const onSubmit = async (values: CreateDisciplineValues) => {
    const name = values.name.trim();
    const existing = disciplines.find((d) => d.name.toLowerCase() === name.toLowerCase());
    let discipline: Pick<Discipline, "id" | "name"> | undefined = existing;
    if (!discipline) {
      try {
        discipline = await createDiscipline.mutateAsync({
          name,
          description: values.description.trim() || undefined,
          category: values.category || undefined,
          color: values.color || undefined,
          organization_id: organizationId,
        });
      } catch {
        // useCreateDiscipline toasts the failure; the MutationCache reports it.
        return;
      }
    }
    onCreated(discipline);
    onOpenChange(false);
  };

  return (
    <StandardDialog open={open} onOpenChange={onOpenChange}>
      <StandardDialogContent size="sm">
        <StandardDialogHeader>
          <StandardDialogTitle>{t("form.discipline.createTitle")}</StandardDialogTitle>
          <StandardDialogDescription>{t("form.discipline.createDescription")}</StandardDialogDescription>
        </StandardDialogHeader>
        <Form {...form}>
          <form
            className="contents"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit(onSubmit)(e);
            }}
          >
            <StandardDialogBody className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <LabelWithError label={t("form.discipline.name")} error={fieldState.error?.message} />
                    <FormControl>
                      <AutocompleteCombobox
                        options={disciplines.map((d) => ({
                          id: d.id,
                          name: d.name,
                          value: d.name,
                          color: d.color,
                          badge: d.category ?? undefined,
                        }))}
                        value={field.value}
                        onValueChange={field.onChange}
                        placeholder={t("form.discipline.namePlaceholder")}
                        emptyMessage={t("form.discipline.nameEmpty")}
                        allowCreate
                        createLabel={(value) => t("form.discipline.createOption", { name: value })}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("form.discipline.description")}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("form.discipline.category")}</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t("form.discipline.categoryPlaceholder")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {DISCIPLINE_CATEGORIES.map((category) => (
                          <SelectItem key={category} value={category}>
                            {t(`form.disciplineCategories.${category}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="color"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("form.discipline.color")}</FormLabel>
                    <FormControl>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={field.value}
                          onChange={field.onChange}
                          className="w-16 h-10 p-1"
                          aria-label={t("form.discipline.color")}
                        />
                        <Input {...field} className="flex-1" />
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />
            </StandardDialogBody>
            <StandardDialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t("common:cancel")}
              </Button>
              <Button type="submit" disabled={createDiscipline.isPending}>
                {createDiscipline.isPending ? t("form.discipline.creating") : t("form.discipline.create")}
              </Button>
            </StandardDialogFooter>
          </form>
        </Form>
      </StandardDialogContent>
    </StandardDialog>
  );
}
